import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_V1, authFetch } from '../constants';

interface NavItem {
  label: string;
  path: string;
  liveIndicator?: boolean;
  badge?: number;
  dotColor?: string;
}

interface NavSection {
  header: string;
  items: NavItem[];
}

export type { NavItem, NavSection };

export const NAV_SECTIONS: NavSection[] = [
  {
    header: 'COMMAND',
    items: [
      { label: 'Dashboard', path: '/', dotColor: '#3b82f6' },
      { label: 'My Inbox', path: '/operations', dotColor: '#8b5cf6' },
      { label: 'AI Workspace', path: '/financial-workspace', dotColor: '#06b6d4' },
      { label: 'Agent Tasks', path: '/agent-tasks', dotColor: '#f59e0b' },
    ],
  },
  {
    header: 'CONTRACTS',
    items: [
      { label: 'Contracts', path: '/contracts', dotColor: '#64748b' },
      { label: 'Contract Intelligence', path: '/contracts/intelligence', dotColor: '#64748b' },
      { label: 'Renewals', path: '/contract-renewals', dotColor: '#64748b' },
    ],
  },
  {
    header: 'PROCURE TO PAY',
    items: [
      { label: 'Vendors', path: '/vendors', dotColor: '#64748b' },
      { label: 'Purchase Orders', path: '/purchase-orders', dotColor: '#64748b' },
      { label: 'Goods Receipts', path: '/goods-receipts', dotColor: '#64748b' },
      { label: 'AP Invoices', path: '/ap-invoices', dotColor: '#64748b' },
      // P2P Payments entry consolidated into Treasury -> Payment Runs (2026-05-15).
    ],
  },
  {
    header: 'ORDER TO CASH',
    items: [
      { label: 'Customers', path: '/customers', dotColor: '#64748b' },
      { label: 'Sales Orders', path: '/sales-orders', dotColor: '#64748b' },
      { label: 'AR Invoices', path: '/ar-invoices', dotColor: '#64748b' },
      { label: 'Cash Application', path: '/cash-application', dotColor: '#64748b' },
      { label: 'Collections', path: '/collections', dotColor: '#64748b' },
      { label: 'Credit Risk', path: '/credit-risk', dotColor: '#64748b' },
      { label: 'Commercial', path: '/commercial', dotColor: '#64748b' },
    ],
  },
  {
    header: 'REVENUE RECOGNITION',
    items: [
      { label: 'Revenue Contracts', path: '/revenue-contracts', dotColor: '#64748b' },
      { label: 'Revenue Recognition', path: '/revenue-recognition', dotColor: '#64748b' },
      { label: 'Revenue Waterfall', path: '/revenue-waterfall', dotColor: '#64748b' },
      { label: 'Reconciliation', path: '/revenue-reconciliation', dotColor: '#64748b' },
      { label: 'Contract Reviews', path: '/revenue-reviews', dotColor: '#64748b' },
      { label: 'Step 3 Tracker', path: '/step3-tracker', dotColor: '#64748b' },
      { label: 'SSP Library', path: '/ssp-library', dotColor: '#64748b' },
      { label: 'Progress Tracker', path: '/progress-tracker', dotColor: '#64748b' },
      { label: 'Disclosures (50)', path: '/revenue-disclosures', dotColor: '#64748b' },
      { label: 'Revenue Schedule', path: '/revenue-schedule', dotColor: '#64748b' },
      { label: 'Modifications', path: '/revenue-modifications', dotColor: '#64748b' },
    ],
  },
  {
    header: 'RECORD TO REPORT',
    items: [
      { label: 'GL Workbench', path: '/gl-workbench', dotColor: '#64748b' },
      { label: 'Financial Reports', path: '/financial-reports', dotColor: '#64748b' },
      { label: 'Budget & Planning', path: '/budget', dotColor: '#64748b' },
      { label: 'Planning Studio', path: '/planning', dotColor: '#7c3aed' },
      { label: 'Profitability', path: '/profitability', dotColor: '#64748b' },
      { label: 'Close Management', path: '/close-management', dotColor: '#64748b' },
      { label: 'Equity Management', path: '/equity', dotColor: '#64748b' },
    ],
  },
  {
    header: 'TREASURY',
    items: [
      { label: 'Bank Accounts', path: '/bank-accounts', dotColor: '#64748b' },
      { label: 'Cash Position', path: '/treasury', dotColor: '#64748b' },
      { label: 'Cash Forecast', path: '/cash-forecast', dotColor: '#64748b' },
      { label: 'FX Management', path: '/fx-management', dotColor: '#64748b' },
      { label: 'Bank Reconciliation', path: '/bank-reconciliation', dotColor: '#64748b' },
      { label: 'Payment Runs', path: '/payment-runs', dotColor: '#64748b' },
      { label: 'IC Netting', path: '/ic-netting', dotColor: '#64748b' },
      { label: 'IC Transactions', path: '/ic-transactions', dotColor: '#64748b' },
      { label: 'Debt Management', path: '/debt', dotColor: '#64748b' },
    ],
  },
  {
    header: 'EXPENSE & PROCUREMENT',
    items: [
      { label: 'Expenses', path: '/expenses', dotColor: '#64748b' },
      { label: 'Procurement', path: '/procurement', dotColor: '#64748b' },
      { label: 'Supply Chain', path: '/supply-chain', dotColor: '#64748b' },
    ],
  },
  {
    header: 'ASSET & INVENTORY',
    items: [
      { label: 'Inventory', path: '/inventory', dotColor: '#64748b' },
      { label: 'Fixed Assets', path: '/fixed-assets', dotColor: '#64748b' },
      { label: 'Lease Accounting', path: '/lease-accounting', dotColor: '#64748b' },
    ],
  },
  {
    header: 'PEOPLE & LEGAL',
    items: [
      { label: 'HR / People', path: '/hr', dotColor: '#64748b' },
      { label: 'Legal', path: '/legal', dotColor: '#64748b' },
      { label: 'Insurance', path: '/insurance', dotColor: '#64748b' },
    ],
  },
  {
    header: 'TAX & COMPLIANCE',
    items: [
      { label: 'Tax Management', path: '/tax', dotColor: '#64748b' },
      { label: 'Project Costing', path: '/project-costing', dotColor: '#64748b' },
      { label: 'SOX 404 Report', path: '/sox404-report', dotColor: '#64748b' },
      { label: 'CECL Pools', path: '/cecl-pools', dotColor: '#64748b' },
      { label: 'CECL Disclosure', path: '/cecl-disclosure', dotColor: '#64748b' },
      { label: 'CECL Admin', path: '/cecl-admin', dotColor: '#64748b' },
    ],
  },
  {
    header: 'INTELLIGENCE',
    items: [
      { label: 'CFO Board View', path: '/cfo-board', dotColor: '#3b82f6' },
      { label: 'CFO Dashboard', path: '/cfo-dashboard', dotColor: '#64748b' },
      { label: 'Decision Engine', path: '/decision-engine', dotColor: '#06b6d4' },
      { label: 'Analytics', path: '/analytics', dotColor: '#64748b' },
      { label: 'Workflows', path: '/workflows', dotColor: '#64748b' },
      { label: 'AI Governance', path: '/governance', dotColor: '#64748b' },
      { label: 'Knowledge Library', path: '/knowledge-library', dotColor: '#64748b' },
      { label: 'Platform Intelligence', path: '/intelligence', dotColor: '#64748b' },
      { label: 'Data Quality', path: '/data-quality', dotColor: '#64748b' },
      { label: 'Activity Tracker', path: '/activity-tracker', dotColor: '#64748b' },
    ],
  },
  {
    header: 'CONFIGURATION',
    items: [
      { label: 'Data Management', path: '/data-management', dotColor: '#64748b' },
      { label: 'Configuration', path: '/configuration', dotColor: '#64748b' },
      { label: 'Integrations', path: '/integrations', dotColor: '#64748b' },
      { label: 'User Management', path: '/user-management', dotColor: '#64748b' },
      { label: 'Tenants', path: '/tenants', dotColor: '#64748b' },
      { label: 'DOA Matrix', path: '/doa-matrix', dotColor: '#64748b' },
      { label: 'Onboarding', path: '/onboarding', dotColor: '#64748b' },
      { label: 'Scheduled Jobs', path: '/admin/scheduled-jobs', dotColor: '#64748b' },
    ],
  },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [liveCounts, setLiveCounts] = useState<Record<string, number>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    // COMMAND always open, others start collapsed
    const init: Record<string, boolean> = {};
    NAV_SECTIONS.forEach(s => { init[s.header] = s.header !== 'COMMAND'; });
    return init;
  });

  const toggleSection = (header: string) => {
    setCollapsed(prev => ({ ...prev, [header]: !prev[header] }));
  };

  // Auto-expand the section containing the active route
  useEffect(() => {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        const match = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
        if (match && collapsed[section.header]) {
          setCollapsed(prev => ({ ...prev, [section.header]: false }));
          return;
        }
      }
    }
  }, [location.pathname]);

  const fetchCounts = useCallback(async () => {
    try {
      const [actionsRes, apRes, revReviewsRes] = await Promise.all([
        authFetch(`${API_V1}/actions/stats/summary`).then(r => r.ok ? r.json() : null).catch(() => null),
        authFetch(`${API_V1}/ap/invoices/summary`).then(r => r.ok ? r.json() : null).catch(() => null),
        authFetch(`${API_V1}/revenue/reviews?status=PENDING`).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      const counts: Record<string, number> = {};
      if (actionsRes?.pending) counts['/operations'] = actionsRes.pending;
      if (apRes?.pending_approval) counts['/ap-invoices'] = apRes.pending_approval;
      const pendingReviews = revReviewsRes?.reviews?.length || 0;
      if (pendingReviews > 0) counts['/revenue-reviews'] = pendingReviews;
      setLiveCounts(counts);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={onClose}
      />

      <div className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Go to home"
          style={{
            padding: '16px 16px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
          }}
          onClick={() => handleNav('/')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNav('/'); } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
              letterSpacing: '-0.5px',
            }}>
              AW
            </div>
            <div className="sidebar-label">
              <div style={{
                fontSize: 14, fontWeight: 700, color: '#f1f5f9',
                letterSpacing: '-0.3px', lineHeight: 1.2,
              }}>
                Agent Workforce
              </div>
              <div style={{
                fontSize: 9, fontWeight: 500, color: '#64748b',
                textTransform: 'uppercase' as const, letterSpacing: '0.5px',
              }}>
                Finance Operations Platform
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ marginTop: 4, padding: '0 8px', overflowY: 'auto', flex: 1 }}>
          {NAV_SECTIONS.map(section => (
            <div key={section.header}>
              {/* Section header — clickable to collapse/expand */}
              <div
                className="sidebar-section-header"
                role="button"
                tabIndex={0}
                onClick={() => toggleSection(section.header)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(section.header); } }}
                style={{
                  fontSize: 10, textTransform: 'uppercase' as const, letterSpacing: '1px',
                  color: '#8896a8', padding: '14px 12px 6px', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  userSelect: 'none',
                  borderTop: section.header !== 'COMMAND' ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  marginTop: section.header !== 'COMMAND' ? 6 : 0,
                }}
              >
                <span>{section.header}</span>
                <span style={{
                  fontSize: 8, transition: 'transform 0.2s',
                  transform: collapsed[section.header] ? 'rotate(0deg)' : 'rotate(90deg)',
                  color: '#8896a8',
                }}>&#9654;</span>
              </div>

              {/* Items — collapsible */}
              <div style={{
                maxHeight: collapsed[section.header] ? 0 : 1000,
                overflow: 'hidden',
                transition: 'max-height 0.25s ease',
              }}>
              {section.items.map(item => {
                const active = isActive(item.path);
                return (
                  <div
                    key={`${section.header}-${item.path}-${item.label}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNav(item.path)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNav(item.path); } }}
                    title={item.label}
                    style={{
                      padding: '7px 12px',
                      fontSize: 13,
                      fontWeight: active ? 500 : 400,
                      color: active ? '#f1f5f9' : '#94a3b8',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      borderRadius: 6,
                      background: active ? '#111d32' : 'transparent',
                      borderLeft: active ? '2px solid #3b82f6' : '2px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      textDecoration: 'none',
                      margin: '1px 0',
                    }}
                    onMouseEnter={e => {
                      if (!active) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                        e.currentTarget.style.color = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!active) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#94a3b8';
                      }
                    }}
                  >
                    {/* Dot indicator */}
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                      background: active ? '#3b82f6' : (item.dotColor || '#475569'),
                      opacity: active ? 1 : 0.5,
                    }} />

                    <span className="sidebar-label" style={{ flex: 1 }}>{item.label}</span>

                    {/* Live badge */}
                    {liveCounts[item.path] > 0 && (
                      <span className="sidebar-label" style={{
                        fontSize: 10, fontWeight: 600,
                        background: item.path === '/operations' ? '#ef4444' : '#3b82f6',
                        color: '#fff',
                        padding: '1px 6px', borderRadius: 8, minWidth: 18, textAlign: 'center',
                      }}>
                        {liveCounts[item.path]}
                      </span>
                    )}

                    {/* Live indicator */}
                    {item.liveIndicator && (
                      <span className="sidebar-label" style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: '#22c55e', flexShrink: 0,
                        animation: 'sidebarBreath 3s ease-in-out infinite',
                      }} />
                    )}
                  </div>
                );
              })}
              </div>{/* end collapsible */}
            </div>
          ))}
        </div>

        <style>{`
          @keyframes sidebarBreath {
            0%, 100% { opacity: 1; box-shadow: 0 0 4px #22c55e; }
            50% { opacity: 0.4; box-shadow: 0 0 1px #22c55e; }
          }
        `}</style>
      </div>
    </>
  );
}
